import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Lock } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/forms/TextField';
import { useChangePassword } from '@/hooks/useProfile';
import { getApiErrorDetail } from '@/lib/utils/error.utils';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { changePasswordSchema, type ChangePasswordFormValues } from './password.schema';

const P = UI_STRINGS.PROFILE;
const ICON_SIZE = 16;
const FORM_ID = 'change-password-form';

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "Change Password" modal shared by the account view and edit pages
 * (rendered from `PasswordCard`). Composes the shared `Dialog` primitive.
 */
export function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const changePassword = useChangePassword();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmNewPassword: '' },
  });

  useEffect(() => {
    if (!open) {
      reset();
      changePassword.reset();
    }
    // `changePassword` is deliberately omitted: useMutation returns a new
    // object every render, so including it would re-run this effect (and
    // call .reset()) on every render instead of only on open/close.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset]);

  const onSubmit = handleSubmit((values) => {
    changePassword.mutate(values, { onSuccess: () => onOpenChange(false) });
  });

  const submitError = changePassword.isError
    ? (getApiErrorDetail(changePassword.error) ?? P.CHANGE_PASSWORD_ERROR)
    : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={P.CHANGE_PASSWORD}
      showCloseButton
      footer={
        <>
          {/* Dialog's footer is a plain (non-reversed) flex-col, so the
              first child renders on top - the primary Save action goes
              first to match the "primary on top" visual order
              (ui-guidelines.md §10), same pattern as ConfirmDialog. */}
          <Button type="submit" form={FORM_ID} variant="green" disabled={changePassword.isPending}>
            {changePassword.isPending ? P.SAVING : P.SAVE_CHANGES}
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={changePassword.isPending}
          >
            {P.CANCEL}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {submitError && (
          <p role="alert" className="text-sm text-danger">
            {submitError}
          </p>
        )}
        <TextField
          {...register('currentPassword')}
          label={P.CURRENT_PASSWORD}
          labelVariant="caps"
          type="password"
          autoComplete="current-password"
          icon={<Lock size={ICON_SIZE} />}
          error={errors.currentPassword?.message}
        />
        <TextField
          {...register('newPassword')}
          label={P.NEW_PASSWORD}
          labelVariant="caps"
          type="password"
          autoComplete="new-password"
          icon={<Lock size={ICON_SIZE} />}
          error={errors.newPassword?.message}
        />
        <TextField
          {...register('confirmNewPassword')}
          label={P.CONFIRM_NEW_PASSWORD}
          labelVariant="caps"
          type="password"
          autoComplete="new-password"
          icon={<Lock size={ICON_SIZE} />}
          error={errors.confirmNewPassword?.message}
        />
      </form>
    </Dialog>
  );
}
